import { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Button, cn, ContentActions, messageFromError, toast, unwrapAction } from '@eo/shared';

import { CapacityBoard } from './CapacityBoard';
import { freshnessLabel } from './helpers';
import type { CapacityContentProps } from './types';

export function CapacityContent({
  data,
  isAdmin,
  meId,
  busy,
  onSync,
  onQueryOptions,
}: CapacityContentProps) {
  const [searchParams] = useSearchParams();
  const report = data.report;
  const [syncing, setSyncing] = useState(false);

  const onRefresh = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      unwrapAction(
        await onSync({ month: data.month, query: Object.fromEntries(searchParams.entries()) }),
      );
      toast.success('Capacity refreshed from ClickUp.');
    } catch (err) {
      toast.error(messageFromError(err, 'Refreshing from ClickUp failed. Try again.'));
    } finally {
      setSyncing(false);
    }
  };

  return (
    // No page gutter here: the panel canvas is full-bleed and owns its insets.
    <div className="flex flex-col gap-4 pb-8">
      <ContentActions className="mx-[var(--page-margin)]">
        <span className="text-xs text-text-tertiary">
          {freshnessLabel(report?.sync.syncedAt ?? null)}
        </span>
        <Button variant="secondary" size="sm" disabled={syncing} onClick={() => void onRefresh()}>
          <RefreshCw className={cn('size-4', syncing && 'animate-spin')} />
          {syncing ? 'Refreshing…' : 'Refresh'}
        </Button>
      </ContentActions>

      {!report ? (
        <p className="px-[var(--page-margin)] text-sm text-text-tertiary">No report loaded.</p>
      ) : (
        // The figures on screen belong to the previous pick until the new ones
        // arrive, so they fade rather than pass as current.
        <div
          data-testid="capacity-board"
          aria-busy={busy}
          className={cn('transition-opacity', busy && 'opacity-60')}
        >
          <CapacityBoard
            data={{ ...data, report }}
            isAdmin={isAdmin}
            meId={meId}
            onQueryOptions={onQueryOptions}
          />
        </div>
      )}
    </div>
  );
}
